import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { apiProcess } from 'app/config/axios-interceptor';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState: any = {
  loading: false,
  errorMessage: null,
  entities: [],
  entityApproves: [],
  preApprovals: [],
  entity: {},
  otherComment: [],
  viewing: false,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  entityAdjustment: {},
};

const apiUrl = 'api/approves';

// Actions
type IRequestAction = {
  ownerId: string;
  accountTableId: string;
  planPeriodId: string;
};

export const getEntities = createAsyncThunk(
  'category_approval/fetch_entity_list',
  async ({ userId, searchText, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/approve-categories`, {
      params: {
        userId,
        searchText,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesAdjustment = createAsyncThunk(
  'category_approval/fetch_entity_list_adjustment',
  async ({ userId, searchText, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`api/ajustments/ajustment-categories`, {
      params: {
        userId,
        searchText,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const checkPermission = createAsyncThunk(
  'category_approval/fetcher_check_permission',
  async ({ userId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`api/approves/check-can-user-edit`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const checkPermissionAdjustment = createAsyncThunk(
  'category_approval/fetcher_check_permission_adjustment',
  async ({ userId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`api/ajustments/check-can-user-edit`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getPreApprove = createAsyncThunk(
  'category_approval/fetch_pre_entity_list',
  async ({ ownerId, planPeriodId }: { ownerId: string, planPeriodId: string }) => {
    return apiProcess.get<any[]>(`${apiUrl}/approve-versions`, {
      params: {
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentStatusProcess = createAsyncThunk(
  'approvalLevel/fetch_current_status_process',
  async ({ ownerId, year, planPeriodId }: any) => {
    const requestUrl = `${apiUrl}/states/detail`;
    return apiProcess.get<any>(requestUrl, {
      params: {
        ownerId,
        year,
        planPeriodId,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentStatusProcessAdjustment = createAsyncThunk(
  'category_approval/current_status_process_adjustment',
  async ({ ownerId, year, planPeriodId }: any) => {
    return apiProcess.get<any>(`api/ajustments/states/detail`, {
      params: { ownerId, year, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentProcess = createAsyncThunk(
  'category_approval/current_process',
  async ({ userId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>('api/approves/current-level-function', {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentProcessAdjustment = createAsyncThunk(
  'category_approval/current_process_adjustment',
  async ({ userId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>('api/ajustments/current-level-function', {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const saveDraft = createAsyncThunk(
  'category_approval/save_entity_draft',
  async (payload: any) => {
    return apiProcess.post<any>(`${apiUrl}/draft-save`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const saveDraftAdjustment = createAsyncThunk(
  'category_approval/save_entity_draft_adjustment',
  async (payload: any) => {
    return apiProcess.post<any>(`api/ajustments/draft-save`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesApprove = createAsyncThunk(
  'category_approval/fetch_entity_list_approve',
  async ({ ownerId, accountTableId, planPeriodId }: IRequestAction) => {
    return apiProcess.get<any[]>('api/approves/approve-targets', {
      params: {
        userId: ownerId,
        ownerId: accountTableId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesApproveAdjustment = createAsyncThunk(
  'category_approval/fetch_entity_list_approve_adjustment',
  async ({ ownerId, accountTableId, planPeriodId }: IRequestAction) => {
    return apiProcess.get<any[]>('api/ajustments/ajustment-targets', {
      params: {
        userId: ownerId,
        ownerId: accountTableId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const cancelProcess = createAsyncThunk(
  'category_approval/cancel_approve_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/cancel`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const completeProcess = createAsyncThunk(
  'category_approval/complete_approve_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/agree`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const completeProcessAdjustment = createAsyncThunk(
  'category_approval/complete_approve_process_adjustment',
  async (payload: any) => {
    return apiProcess.post(`api/ajustments/agree`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const rejectProcess = createAsyncThunk(
  'category_approval/complete_approve_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/refuse`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const rejectProcessAdjustment = createAsyncThunk(
  'category_approval/complete_approve_process_adjustment',
  async (payload: any) => {
    return apiProcess.post(`api/ajustments/refuse`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const submitApprove = createAsyncThunk(
  'category_approval/approve_entity_list',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/submit-approval`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const getLimitedProcessApprove = createAsyncThunk(
  'category_approval/list_process_approve_limited',
  async ({ userId, planPeriodId }: { userId: string, planPeriodId: string }) => {
    return apiProcess.get(`${apiUrl}/max-order-level`, {
      params: {
        userId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getLimitedProcessApproveVersion = createAsyncThunk(
  'category_approval/list_process_approve_limited_version',
  async ({ userId, planPeriodId }: { userId: string, planPeriodId: string }) => {
    return apiProcess.get(`${apiUrl}/max-order-level`, {
      params: {
        userId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getLimitedProcessMaxOrderLevelAdjustment = createAsyncThunk(
  'category_approval/list_process_approve_limited_adjustment',
  async (params: { userId: string, planPeriodId: string }) => {
    return apiProcess.get('api/ajustments/max-order-level', {
      params: {
        userId: params.userId,
        planPeriodId: params.planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getLimitedProcessApproveAdjustment = createAsyncThunk(
  'category_approval/fetch_entity_list_approve_adjustment_categories',
  async (params: { userId: string, planPeriodId: string }) => {
    return apiProcess.get('api/ajustments/ajustment-categories', {
      params: {
        userId: params.userId,
        planPeriodId: params.planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getOtherComment = createAsyncThunk(
  'category_approval/other_comment',
  async ({ itemId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/prefeedback-item`, {
      params: {
        itemId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getOtherCommentAdjustment = createAsyncThunk(
  'category_approval/other_comment_adjustment',
  async ({ itemId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any>(`api/ajustments/prefeedback-item`, {
      params: {
        itemId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getItemPendingApprove = createAsyncThunk(
  'category_approval/list_item_pending_approve',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/preview-approval-submission`, payload);
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CategoryApprovalSlice = createEntitySlice({
  name: 'categoryApproval',
  initialState,
  reducers: {
    resetApprovalEntity(state) {
      return { ...state, entityApproves: [] };
    },
    resetCategoryApproveView(state) {
      return {
        ...state,
        loading: false,
        entities: [],
        entityApproves: [],
        preApprovals: [],
        otherComment: [],
      };
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getEntities, getEntitiesAdjustment, getLimitedProcessApproveAdjustment), (state, action) => {
        const { data } = action.payload as any;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getPreApprove), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          preApprovals: data,
        };
      })
      .addMatcher(isFulfilled(getEntitiesApprove, getEntitiesApproveAdjustment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entityApproves: data,
        };
      })

      .addMatcher(isFulfilled(getItemPendingApprove), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(isFulfilled(saveDraft, saveDraftAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(getCurrentStatusProcess), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getCurrentStatusProcessAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.entityAdjustment = action.payload.data;
      })
      .addMatcher(isPending(getCurrentStatusProcessAdjustment), (state, action) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        // Không reset entityAdjustment khi đang pending để tránh mất dữ liệu
        // state.entityAdjustment = {};
      })
      .addMatcher(isRejected(getCurrentStatusProcessAdjustment), (state, action) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.entityAdjustment = {};
      })
      .addMatcher(isFulfilled(getCurrentProcess, getCurrentProcessAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(completeProcess, completeProcessAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(rejectProcess), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(submitApprove), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(getOtherComment, getOtherCommentAdjustment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherComment: data,
        };
      })
      .addMatcher(isPending(getCurrentStatusProcess, getCurrentProcess, getCurrentProcessAdjustment), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        // Không reset entity khi đang pending để tránh mất dữ liệu
        // state.entity = {};
      })
      .addMatcher(isPending(saveDraft, submitApprove, completeProcess, rejectProcess, rejectProcessAdjustment, saveDraftAdjustment, completeProcessAdjustment), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getEntities, getEntitiesApprove, getEntitiesApproveAdjustment, getPreApprove, getEntitiesAdjustment, getLimitedProcessApproveAdjustment), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      });
  },
});

export const { reset, resetApprovalEntity, resetCategoryApproveView } = CategoryApprovalSlice.actions;

// Reducer
export default CategoryApprovalSlice.reducer;
